import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

import {
  BackButton,
  ChaptersButton,
  MuteButton,
  PauseButton,
  PlayButton,
  SkipButton,
  SoundButton,
  SubtitlesButton,
} from "./Buttons";
import { Context } from "../../store";
import {
  toggleChapters,
  setIndex,
  toggleLanguages,
} from "../../store/reducer";
import { configurePlyr, moveCaptions } from "./utils";
import { useTranslation } from "react-i18next";

const CONTROLS_DISPLAY_TIME = 3_000;

const Controls = () => {
  const [{ player, index, currentItem }, dispatch] =
    useContext(Context);
  const { t } = useTranslation();
  const [configured, setConfigured] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showingControls, setShowingControls] =
    useState(false);
  const currentTimeout = useRef();

  const moveListener = useCallback(() => {
    if (currentTimeout.current) {
      clearTimeout(currentTimeout.current);
    }
    if (!showingControls) {
      setShowingControls(true);
    }
    const captions = moveCaptions();
    currentTimeout.current = setTimeout(() => {
      setShowingControls(false);
      if (captions) {
        captions.classList.remove("moveY");
      }
    }, CONTROLS_DISPLAY_TIME);
  }, [currentTimeout, showingControls, setShowingControls]);

  const onChaptersClick = () => {
    dispatch(toggleChapters());
  };

  const togglePlaying = () => {
    player.togglePlay();
  };

  const setPlayingTo = (val) => () => {
    setPlaying(val);
  };

  const toggleMute = () => {
    player.muted = !player.muted;
  };

  const onSkip = () => {
    player.currentTime = player.duration;
  };

  const onBack = () => {
    if (player.currentTime > 3) {
      player.currentTime = 0;
    } else if (index > 0) {
      dispatch(setIndex(index - 1));
    }
  };

  const onSubtitlesClick = () => {
    dispatch(toggleLanguages());
  };

  useEffect(() => {
    if (player && !configured) {
      const onVolumeChange = () => {
        setMuted(player.muted);
      };

      setConfigured(true);
      configurePlyr(player, [
        ["play", setPlayingTo(true)],
        ["pause", setPlayingTo(false)],
        ["ended", setPlayingTo(false)],
        ["volumechange", onVolumeChange],
      ]);
      document.addEventListener("mousemove", moveListener);
    }
  }, [player, configured, setConfigured, moveListener]);

  return (
    <div
      id="custom_controls"
      className={classNames(
        "plyr--full-ui absolute inset-x-0 z-10 md:flex justify-center items-center px-6 pb-6 md:hover:opacity-100 transition",
        { "opacity-0": !showingControls }
      )}
    >
      <ChaptersButton
        onClick={onChaptersClick}
        className="md:mx-4 md:mb-0 mb-3  inline-block"
      />
      <div className="flex items-center md:mx-0 -ml-4">
        <BackButton
          onClick={onBack}
          className="mx-4 inline-block"
        />
        {playing ? (
          <PauseButton onClick={togglePlaying} />
        ) : (
          <PlayButton onClick={togglePlaying} />
        )}
        <SkipButton
          onClick={onSkip}
          className="mx-4 inline-block"
        />
        {muted ? (
          <MuteButton
            onClick={toggleMute}
            className="ml-2 mr-6 inline-block"
          />
        ) : (
          <SoundButton
            onClick={toggleMute}
            className="ml-2 mr-6 inline-block"
          />
        )}
        <SubtitlesButton onClick={onSubtitlesClick} />
      </div>
      <div className="progress_timer flex items-end flex-1 md:ml-6">
        <div className="progress_wrapper flex-1 mx-4">
          <p className="text-white hidden md:block">
            {t(currentItem.title)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Controls;
